import { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useSimpleQuoteContext } from "@/contexts/SimpleQuoteContext";
import { Badge } from "@/components/ui/badge";

// Custom hook for debounce functionality
function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function QuoteManagerFilters() {
  const {
    searchQuery,
    setSearchQuery,
    categoryFilter,
    setCategoryFilter,
    filterUncategorized,
    setFilterUncategorized,
    showDuplicates,
    setShowDuplicates,
    pageSize,
    setPageSize,
    isLoading,
    categories,
  } = useSimpleQuoteContext();

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Apply debouncing to the search input
  const debouncedSearchQuery = useDebounce(localSearchQuery, 500);

  useEffect(() => {
    if (debouncedSearchQuery !== searchQuery) {
      setSearchQuery(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery, searchQuery, setSearchQuery]);

  const handleClearFilters = () => {
    setLocalSearchQuery("");
    setSearchQuery("");
    setCategoryFilter("all");
    setFilterUncategorized(false);
    setShowDuplicates(false);
    setPageSize(10);
  };

  const hasActiveFilters = searchQuery || categoryFilter !== "all" || filterUncategorized || showDuplicates || pageSize !== 10;

  return (
    <div className="space-y-4">
      <div className="flex flex-col space-y-4 md:flex-row md:items-end md:space-y-0 md:space-x-4">
        <div className="flex-1 space-y-1">
          <Label htmlFor="search">Search</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              id="search"
              type="search"
              placeholder="Search quotes, authors, sources..."
              className="pl-8"
              value={localSearchQuery}
              onChange={(e) => setLocalSearchQuery(e.target.value)}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="flex space-x-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="uncategorized"
              checked={filterUncategorized}
              onCheckedChange={setFilterUncategorized}
              disabled={isLoading}
            />
            <Label htmlFor="uncategorized">Uncategorized</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="duplicates"
              checked={showDuplicates}
              onCheckedChange={setShowDuplicates}
              disabled={isLoading}
            />
            <Label htmlFor="duplicates">Duplicates</Label>
          </div>
        </div>

        <div className="w-full md:w-[180px] space-y-1">
          <Label htmlFor="category">Category</Label>
          <Select
            value={categoryFilter}
            onValueChange={setCategoryFilter}
            disabled={filterUncategorized || isLoading}
          >
            <SelectTrigger id="category">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-full md:w-[140px] space-y-1">
          <Label htmlFor="pageSize">Rows per page</Label>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => setPageSize(Number(value))}
            disabled={isLoading}
          >
            <SelectTrigger id="pageSize">
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5 rows</SelectItem>
              <SelectItem value="10">10 rows</SelectItem>
              <SelectItem value="20">20 rows</SelectItem>
              <SelectItem value="50">50 rows</SelectItem>
              <SelectItem value="100">100 rows</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearFilters}
            disabled={isLoading}
          >
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2">
          {searchQuery && (
            <Badge variant="secondary">
              Search: {searchQuery}
            </Badge>
          )}
          {categoryFilter !== "all" && (
            <Badge variant="secondary">
              Category: {categories.find(c => c.id === categoryFilter)?.name || categoryFilter}
            </Badge>
          )}
          {filterUncategorized && (
            <Badge variant="secondary">
              Uncategorized
            </Badge>
          )}
          {showDuplicates && (
            <Badge variant="secondary">
              Duplicates
            </Badge>
          )}
          {pageSize !== 10 && (
            <Badge variant="secondary">
              {pageSize} rows per page
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
